import {ChangeDetectionStrategy, Component, effect, EventEmitter, inject, input, OnInit, Output, ViewChild} from '@angular/core';
import {MatPaginator, MatPaginatorModule, PageEvent} from '@angular/material/paginator';
import {MatSort, MatSortModule, Sort} from '@angular/material/sort';
import {MatTableDataSource, MatTableModule} from '@angular/material/table';
import {MatInputModule} from '@angular/material/input';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIcon} from '@angular/material/icon';
import {MatIconButton} from '@angular/material/button';
import {PersonBadgeComponent} from '../../../common/person-picker/person-badge/person-badge.component';
import {Task} from '../../../../models/task';
import dayjs from 'dayjs';
import {StatusBadgeComponent} from '../../../common/status-badge/status-badge.component';
import {GMDatePipe} from '../../../common/date.pipe';
import {TaskRelationship} from '../../../../models/task-relationship';
import {Contact} from '../../../../models/contact';
import {environment} from '../../../../environments/environment';
import {MatDialog} from '@angular/material/dialog';
import {AssignPersonComponent} from '../../../common/assign-person/assign-person.component';
import {TaskService} from '../../../services/task.service';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {Router} from '@angular/router';
import {CS} from '../../../../models/cs';
import {RelateContactCsComponent} from '../../../common/relate-contact-cs/relate-contact-cs.component';
import {NgStyle} from '@angular/common';
import {toFilter} from '../../../utils';

export type TaskRelation = TaskRelationship & {
  contact?: Contact;
  cs?: CS;
}

export type TaskData = Task & {
  relationships?: TaskRelation[];
  loadingAssignee?: boolean;
  _assignee?: string;
  _relationship?: string;
  _description?: string;
  _id?: string;
  _name?: string;
  isLate?: boolean;
}

@Component({
  selector: 'app-task-table',
  styleUrl: 'task-table.component.scss',
  templateUrl: 'task-table.component.html',
  imports: [MatFormFieldModule, MatInputModule, MatTableModule, MatSortModule,
    MatPaginatorModule, MatIcon, MatIconButton, PersonBadgeComponent,
    StatusBadgeComponent, GMDatePipe, MatProgressSpinner,
    RelateContactCsComponent,NgStyle
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TaskTable implements OnInit {
  displayedColumns: string[] = ['_id', '_name', '_relationship', '_assignee', 'status', 'deadline', 'open'];
  dataSource: MatTableDataSource<TaskData> = new MatTableDataSource([] as TaskData[]);

  @ViewChild(MatPaginator) paginator?: MatPaginator;
  @ViewChild(MatSort) sort?: MatSort;

  @Output() openDetails: EventEmitter<string> = new EventEmitter();
  @Output() pageChange: EventEmitter<PageEvent> = new EventEmitter();
  @Output() sortChange: EventEmitter<Sort> = new EventEmitter();

  values = input.required<Task[]>();
  toBeDeleted = input<string>();
  totalItems = input<number>(0);
  pageSize = input<number>(10);

  taskService = inject(TaskService);
  router = inject(Router);

  constructor() {
    effect(() => {
      this.update();
    });
  }

  openRelation(relationshipRoute: string, newTab: boolean = false) {
    if (newTab) {
      window.open(window.location.origin + relationshipRoute, '_blank');
      return;
    }
    this.router.navigate([relationshipRoute]);
  }

  update() {
    this.dataSource.data = this.values()
      .filter((value) => value.id !== this.toBeDeleted())
      .map((value) => this.createNewTask(value));
  }

  ngOnInit() {
    this.update();
  }

  onSortChange(event: Sort) {
    this.sortChange.emit(event);
  }

  onPageChange(event: PageEvent) {
    this.pageChange.emit(event);
  }

  createNewTask(task: Task): TaskData {
    const relatitionships = task.relationships?.map((relationship) => {
      return { ...relationship }
    });
    // closedAt is the date when the task status was set to done, if it is not closed, it is the current date
    const referenceDate = task.closedAt ? dayjs.unix(task.closedAt) : dayjs();
    return {
      ...task,
      relationships: relatitionships,
      loadingAssignee: false,
      _name: toFilter(task.name),
      _id: toFilter(task.alias),
      _assignee: toFilter(task.assigneeUser?.name.toLowerCase() ?? 'Unassigned'),
      _relationship: '',
      _description: toFilter(task.delta ? task.delta.map((d: any) => d.insert).join(' ').replaceAll('\n',' ') : ''),
      isLate: task.deadline == null ? false : dayjs.unix(task.deadline).isBefore(referenceDate, 'day')
    };
  }

  readonly dialog = inject(MatDialog);

  openDialog(task: TaskData): void {
    task.loadingAssignee = true;
    const dialogRef = this.dialog.open(AssignPersonComponent, {
      width: '400px',
      height: '100px',
      data: {},
      panelClass: 'custom-dialog-container'
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result && result.assignee) {
        await this.taskService.updateTaskAssignee(task.id, result.assignee);
      }
      task.loadingAssignee = false;
    });
  }

  protected readonly environment = environment;
  protected readonly window = window;
}


